import { useCallback, useContext } from 'react'
import ModalContext from 'contexts/ModalContext'
import type { AccountAccessModalResult } from 'components/Account/AccountAccess/AccountAccessModal.types'
import AccountAccessModal from 'components/Account/AccountAccess/AccountAccessModal'
import type { AccountAccessModalProps } from 'components/Account/AccountAccess/AccountAccessModal'
import { ChertoExperiments } from 'constants/experiments'
import useExperiment from 'hooks/Experiments/useExperiment'
import { useIsMobileDevice } from 'hooks/useIsMobileDevice'
import { isFlashHotelOffer } from 'lib/offer/offerTypes'
import { isMaldivesCharterBundleOffer } from 'lib/charterFlights/charterBundleOfferPageUtils'
import type { FlashOfferSignUpContentProps } from './FlashOfferSignUpContent'

const MANUAL_ACTIVATION_OPTIONS = { canActivate: false }
const DEFAULT_ACCOUNT_ACCESS_PROPS = {}
const CHARTER_OFFER_OPTIONS = { requireFlightsEnabled: false }
const DISMISSED_RESULT: AccountAccessModalResult = { loggedIn: false }

/** Keep allocation on the modal-open event, shared by every flash hotel sign-up entry point. */
function useFlashOfferSignUpModal() {
  const showModal = useContext(ModalContext)
  const isMobileDevice = useIsMobileDevice()
  const { isTurnedOn, variation, triggerActivation } = useExperiment(
    ChertoExperiments.croFlashOfferPageSignUpModal,
    MANUAL_ACTIVATION_OPTIONS,
  )

  return useCallback(async(
    offer: App.HotelOffer | App.VillaOffer | undefined,
    packageViewOrPackage: App.OfferPackageOptionView | App.HotelPackage | undefined,
    accountAccessProps: Omit<AccountAccessModalProps, 'flashOfferSignUp'> = DEFAULT_ACCOUNT_ACCESS_PROPS,
    checkInDate?: string,
  ) => {
    let flashOfferSignUp: FlashOfferSignUpContentProps | undefined
    if (isMobileDevice && isTurnedOn && isFlashHotelOffer(offer) && !isMaldivesCharterBundleOffer(offer, true, CHARTER_OFFER_OPTIONS) && packageViewOrPackage) {
      const packageView = 'hotelPrice' in packageViewOrPackage ? packageViewOrPackage : undefined
      const pkg = 'hotelPrice' in packageViewOrPackage ? packageViewOrPackage.package : packageViewOrPackage
      triggerActivation()
      if (variation === 'variant') {
        flashOfferSignUp = {
          offer,
          pkg,
          packagePricing: packageView?.pricing,
          packageMemberPricing: packageView?.memberPricing,
          checkInDate,
        }
      }
    }

    const result = await showModal<AccountAccessModalResult | undefined>(<AccountAccessModal
      {...accountAccessProps}
      initialMode={flashOfferSignUp ? 'login' : accountAccessProps.initialMode}
      flashOfferSignUp={flashOfferSignUp}
    />)
    return result ?? DISMISSED_RESULT
  }, [isMobileDevice, isTurnedOn, showModal, triggerActivation, variation])
}

export default useFlashOfferSignUpModal
